import React, { useEffect, useState } from 'react';

import { Accordion } from '@buildoutinc/blueprint-react/ui/Accordion';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { faSparkle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { post } from 'helpers/api';
import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';
import { AI_HTML_OPTIONS } from 'components/ai/html';
import type { Metrics } from 'components/ai/assistant/types';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import SanitizedHTML from 'components/ui/sanitized_html';
import ModelComparison, { type ModelOption } from './model_comparison';
import SummaryBody from './summary_body';

interface Props {
  path: string;
  errorMessage: string;
  defaultOpen?: boolean;
  collapseKey?: string | number;
  features?: { adminOnly: boolean };
  availableModels?: ModelOption[];
}

const ITEM_VALUE = 'summary';

export default function AiSummary({ path, errorMessage, defaultOpen = true, collapseKey, features, availableModels = [] }: Props) {
  const adminOnly = features?.adminOnly ?? false;
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openItems, setOpenItems] = useState<string[]>(defaultOpen ? [ITEM_VALUE] : []);
  const [prevCollapseKey, setPrevCollapseKey] = useState(collapseKey);
  const [modalOpen, setModalOpen] = useState(false);
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  const { subscribe, unsubscribe } = useOneOffSubscription();

  useEffect(() => {
    subscribe<{ success: true; summary: string; metrics?: Metrics } | { success: false }>({
      async connected(requestId) {
        const response = await post(path, { requestId });
        if (response?.summary) {
          // Cache hit — no worker will broadcast.
          unsubscribe();
          setSummary(response.summary);
          setMetrics(response.metrics ?? null);
          setLoading(false);
        }
      },
      connectedError() {
        setError(true);
        setLoading(false);
      },
      received(payload) {
        if (payload.success) {
          setSummary(payload.summary);
          setMetrics(payload.metrics ?? null);
        } else {
          setError(true);
        }
        setLoading(false);
      },
    });
  }, [path, subscribe, unsubscribe]);

  if (collapseKey !== prevCollapseKey) {
    setPrevCollapseKey(collapseKey);
    setOpenItems([]);
  }

  const summaryContent = (
    <>
      {loading && <LoadingSpinner size="md" skipTopBuffer />}
      {!loading && error &&
        <div className="text-muted">{errorMessage}</div>
      }
      {!loading && !error &&
        <SanitizedHTML className="ai-assistant-prose" html={summary} {...AI_HTML_OPTIONS} />
      }
    </>
  );

  return (
    <>
      <Accordion
        className="shadow rounded p-3 bg-purple-heart-50 border-purple-heart-100"
        value={openItems}
        variant="inline"
        onValueChange={next => setOpenItems(next as string[])}
      >
        <Accordion.Item className="d-flex flex-column gap-3" value={ITEM_VALUE}>
          <Accordion.Trigger className="bg-purple-heart-50">
            <FontAwesomeIcon
              className="text-purple-heart-950"
              icon={faSparkle}
              onClick={adminOnly ? ((e: React.MouseEvent<SVGSVGElement>) => {
                e.preventDefault();
                e.stopPropagation();
                setModalOpen(true);
              }) : undefined}
            />
            AI Summary
          </Accordion.Trigger>
          <Accordion.Content className="p-0">
            <div className="d-flex flex-column gap-3">
              <div className="bg-white rounded p-3">
                {summaryContent}
              </div>
              <div className="text-muted fs-xs">
                Summary is generated by AI. It can contain mistakes.
              </div>
            </div>
          </Accordion.Content>
        </Accordion.Item>
      </Accordion>

      {adminOnly &&
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Modal.Content centered size="xl">
            <Modal.Header>
              <Modal.Title>
                <FontAwesomeIcon className="text-purple-heart-950 me-2" icon={faSparkle} />
                AI Summary
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="d-flex flex-column flex-md-row gap-4">
                <div className="flex-grow-1 d-flex flex-column" style={{ flexBasis: 0, minWidth: 0 }}>
                  <div className="fw-semibold mb-2">Summary</div>
                  <SummaryBody
                    error={error}
                    errorMessage={errorMessage}
                    loading={loading}
                    metrics={metrics}
                    summary={summary}
                  />
                </div>
                <div className="flex-grow-1 d-flex flex-column border-start ps-md-4" style={{ flexBasis: 0, minWidth: 0 }}>
                  <div className="fw-semibold mb-2">Compare a model</div>
                  <ModelComparison errorMessage={errorMessage} models={availableModels} path={path} />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button label="Close" variant="secondary" onClick={() => setModalOpen(false)} />
            </Modal.Footer>
          </Modal.Content>
        </Modal>
      }
    </>
  );
}
